import { type PropsWithChildren, useEffect, useMemo, useState } from "react";

import copy from "copy-to-clipboard";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { LoadingSlideNoText, TitleSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type FlyoutTabId } from "@features/sales-v3/types";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager/use-design-element-manager";
import {
  type GetCampaign,
  type GetCampaignVariables,
} from "@north-beam/nb-client-side";
import {
  type CompareDateRange,
  type DateInterval,
  type DateRangeChoiceV3,
  deslugify,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
  level,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { GET_CAMPAIGN } from "@pages/objects/ad-object-editor";
import { reportStateToGQLVariables } from "@pages/objects/report-state";
import { TertiaryButton } from "@shared/buttons";
import { Flyout as FlyoutComponent } from "@shared/flyouts";

import { Creatives } from "./creatives";
import { Details } from "./details";
import { Journey } from "./journey";
import { Performance } from "./performance";
import { Tab } from "./tab";

const DRAWER_WIDTH_DEFAULT = 500;
const DRAWER_WIDTH_WIDE = Math.min(1500, window.innerWidth - 120);

const getDrawerWidth = (tab: FlyoutTabId) =>
  tab === "journey" ? DRAWER_WIDTH_WIDE : DRAWER_WIDTH_DEFAULT;

export interface FlyoutConfig {
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  dateRangeInterval: DateInterval;
  compareDateRange: CompareDateRange | null;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularityV2;
  flyoutId?: string | null;
  flyoutTab: FlyoutTabId;
  setFlyoutId: (val: string | null) => void;
  setFlyoutTab: (val: FlyoutTabId) => void;
}

interface Props {
  config: FlyoutConfig;
  hideAdObjectPageLink?: boolean;
}

export const Flyout = ({ config, hideAdObjectPageLink }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [drawerWidth, setDrawerWidth] = useState(
    getDrawerWidth(config.flyoutTab),
  );

  const isFlyoutOpen = Boolean(config.flyoutId);

  useEffect(() => {
    setDrawerWidth(getDrawerWidth(config.flyoutTab));
  }, [isFlyoutOpen, config.flyoutTab]);

  const closeFlyout = () => {
    prettyLogEventPage({
      domain: "Flyout",
      action: "onClick",
      additionalActionInfo: "Flyout closed",
    });

    config.setFlyoutId(null);
    config.setFlyoutTab("details");
  };

  return (
    <FlyoutComponent
      open={isFlyoutOpen}
      direction="right"
      className="overflow-allowed col text-sm"
      onClose={closeFlyout}
      drawerWidth={drawerWidth}
    >
      {config.flyoutId && isFlyoutOpen && (
        <FlyoutContent
          config={{ ...config, flyoutId: config.flyoutId }}
          onClose={closeFlyout}
          onDrawerWidthChange={setDrawerWidth}
          hideAdObjectPageLink={hideAdObjectPageLink}
        />
      )}
    </FlyoutComponent>
  );
};

const FlyoutPlaceholder = ({
  children,
  onClose,
}: PropsWithChildren<{ onClose(): void }>) => (
  <div className="h-full flex flex-col p-6">
    <button
      className="text-2xl hover:text-cosmic+1 self-end mt-1"
      onClick={onClose}
    >
      <i className="fa-regular fa-times" />
    </button>
    {children}
  </div>
);

const FlyoutContent = ({
  config,
  hideAdObjectPageLink,
  onClose,
  onDrawerWidthChange,
}: {
  config: Omit<FlyoutConfig, "flyoutId"> & { flyoutId: string };
  hideAdObjectPageLink?: boolean;
  onClose(): void;
  onDrawerWidthChange(value: number): void;
}) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const anchor = todayLocalAsISODate();
  const gqlVariables = useMemo(() => {
    return reportStateToGQLVariables(
      {
        dateRange: config.dateRange,
        // TODO: Fix this
        compareDateRange: config.compareDateRange as any,
        attributionModel: config.attributionModel,
        attributionWindow: config.attributionWindow,
        nbAccountingMode: config.accountingMode,
        timeGranularity: config.timeGranularity,
      },
      anchor,
    );
  }, [config, anchor]);
  const toast = useToasts();

  const { loading, data, refetch } = useNorthbeamQuery<
    GetCampaign,
    GetCampaignVariables
  >(GET_CAMPAIGN, {
    variables: {
      id: config.flyoutId,
      ...gqlVariables,
    },
    useHeavy: true,
  });

  const [history, setHistory] = useState<string[]>([]);
  const { designElementManager } = useDesignElementManager();

  if (loading) {
    return (
      <FlyoutPlaceholder onClose={onClose}>
        <LoadingSlideNoText />
      </FlyoutPlaceholder>
    );
  }

  const adObject = data?.me.adObject;

  if (!adObject) {
    return (
      <FlyoutPlaceholder onClose={onClose}>
        <TitleSlide>
          Could not find this Traffic Source. Please contact Northbeam support!
        </TitleSlide>
      </FlyoutPlaceholder>
    );
  }

  const adKey = deslugify(adObject.id);
  const adObjectLevel = level(adKey);
  const platformLabel =
    adObject.labelsDigest?.find((e) => e.key === "Platform (Northbeam)")
      ?.value ?? "";
  const icon = designElementManager
    .getDesignElement(platformLabel)
    .getComponent({ size: "32px", className: "mr-2" });

  const goForward = (id: string) => {
    prettyLogEventPage({
      domain: "Flyout",
      action: "onClick",
      additionalActionInfo: "Hierarchy Go Forward",
    });

    history.push(adObject.id);
    config.setFlyoutId(id);
    config.setFlyoutTab("details");
  };

  const goBack = () => {
    const id = [...history].pop();

    if (id) {
      prettyLogEventPage({
        domain: "Flyout",
        action: "onClick",
        additionalActionInfo: "Hierarchy Go Back",
      });

      setHistory(history.filter((e) => e !== id));
      config.setFlyoutId(id);
      config.setFlyoutTab("details");
    }
  };

  return (
    <div className="px-6 flex flex-col h-full">
      <div className="flex-1">
        <div className="sticky top-0 bg-white pt-6 z-20">
          {history.length > 0 && (
            <TertiaryButton className="mb-3 px-2.5 font-bold" onClick={goBack}>
              <i className="fa-regular fa-arrow-left font-bold" /> BACK
            </TertiaryButton>
          )}
          <h1 className="flex items-center" title={adObject.name}>
            {icon}
            <GenericTooltip
              content="Click to copy to clipboard"
              placement="bottom"
              noInfoCircle
              className="flex-1 truncate mr-4 hover:text-primary cursor-pointer"
            >
              <div
                className="flex-1 truncate"
                onClick={() => {
                  copy(adObject.name);
                  toast.addToast("COPIED AD NAME TO CLIPBOARD", {
                    appearance: "success",
                  });
                }}
              >
                {adObject.name}
              </div>
            </GenericTooltip>
            <button className="shrink-0 hover:text-cosmic+1" onClick={onClose}>
              <i className="fa-regular fa-times" />
            </button>
          </h1>
          <div className="font-light text-gray+1 capitalize">
            {adObjectLevel}
          </div>
          <div className="border-b border-gray-3 mt-8">
            <div className="flex justify-around max-w-[452px]">
              <Tab
                isActive={config.flyoutTab === "details"}
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Flyout",
                    action: "onClick",
                    additionalActionInfo: "Flyout tab: details",
                  });

                  onDrawerWidthChange(getDrawerWidth("details"));
                  config.setFlyoutTab("details");
                }}
              >
                Details
              </Tab>
              <Tab
                isActive={config.flyoutTab === "performance"}
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Flyout",
                    action: "onClick",
                    additionalActionInfo: "Flyout tab: performance",
                  });

                  onDrawerWidthChange(getDrawerWidth("performance"));
                  config.setFlyoutTab("performance");
                }}
              >
                Performance
              </Tab>
              {adObject.hierarchy && (
                <Tab
                  isActive={config.flyoutTab === "creatives"}
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo: "Flyout tab: creatives",
                    });

                    onDrawerWidthChange(getDrawerWidth("creatives"));
                    config.setFlyoutTab("creatives");
                  }}
                >
                  Creatives
                </Tab>
              )}
              {adObject.hasCommonTouchpoints && (
                <Tab
                  isActive={config.flyoutTab === "journey"}
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo: "Flyout tab: journey",
                    });

                    onDrawerWidthChange(getDrawerWidth("journey"));
                    config.setFlyoutTab("journey");
                  }}
                >
                  Journey
                </Tab>
              )}
            </div>
          </div>
        </div>
        {config.flyoutTab === "details" && (
          <Details
            adObject={adObject}
            adObjectLevel={adObjectLevel}
            platformLabel={platformLabel}
            onRequestReload={async () => {
              refetch && (await refetch());
            }}
            onChangeAdObject={goForward}
          />
        )}
        {config.flyoutTab === "performance" && (
          <Performance
            adObject={adObject}
            adObjectLevel={adObjectLevel}
            config={config}
            onChangeAdObject={goForward}
          />
        )}
        {config.flyoutTab === "creatives" && adObject.hierarchy && (
          <Creatives adObject={adObject} adObjectLevel={adObjectLevel} />
        )}
        {config.flyoutTab === "journey" && (
          <Journey adObject={adObject} dateRange={config.dateRangeInterval} />
        )}
      </div>
      {adObject.id && !hideAdObjectPageLink && (
        <div className="sticky bottom-0 bg-white pt-4 pb-5 px-6 -ml-6 -mr-6 z-20">
          <Link
            className="button primary-button sm inline-block no-underline"
            to={`../objects/${adObject.id}`}
            relative="path"
            target="_blank"
            rel="noreferrer"
          >
            VIEW AD OBJECT PAGE
          </Link>
        </div>
      )}
    </div>
  );
};
